import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import {
  FaDrum, FaCheckCircle, FaClock, FaTrophy,
  FaStar, FaArrowRight, FaMicrophoneAlt
} from 'react-icons/fa';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };

const faqs = [
  { q: "What are the prerequisites for the advanced tabla course?", a: "You should be able to confidently perform teentaal, keherwa, and at least 3 other taals, and have completed 2+ years of serious tabla study." },
  { q: "What is ABGMVM Visharad certification?", a: "Akhil Bharatiya Gandharva Mahavidyalaya Mandal (ABGMVM) is India's premier classical music examination body. The Visharad degree is a prestigious diploma equivalent to a university degree in music. This course prepares you for that examination." },
  { q: "How long is the advanced tabla course?", a: "The advanced course is a 1-year intensive programme with one-on-one sessions with Maestro Atharva Kagalkar." },
  { q: "Will I be able to perform at concerts after this course?", a: "Yes. The course specifically prepares you to perform a complete 45-minute tabla solo concert and to accompany vocalists, instrumentalists, and classical dancers professionally." },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a }
  }))
};

const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "Advanced Tabla Mastery — Concert-Level Training",
  "description": "Advanced tabla training by Maestro Atharva Kagalkar at Taalsetu. Master the art of tabla solo performance, complex taals, and the Banaras and Delhi gharana styles.",
  "provider": {
    "@type": "MusicSchool",
    "@id": "https://www.taalsetu.com/#organization",
    "name": "Taalsetu Music Academy",
    "sameAs": "https://www.taalsetu.com"
  },
  "instructor": {
    "@type": "Person",
    "name": "Atharva Kagalkar",
    "jobTitle": "Tabla Maestro"
  },
  "url": "https://www.taalsetu.com/courses/advanced/",
  "image": "https://www.taalsetu.com/logo512.png",
  "courseMode": ["online", "onsite"],
  "educationalLevel": "Advanced",
  "inLanguage": "hi-IN",
  "offers": {
    "@type": "Offer",
    "category": "Paid",
    "availability": "https://schema.org/InStock"
  }
};

const modules = [
  { title: "Gharana Styles", desc: "Deep exploration of Delhi, Banaras, Ajrada, and Farukhabad gharana styles — their distinct strokes, compositions, and philosophy." },
  { title: "Extended Compositions", desc: "Long-form kaidas, relas, and gats spanning 50+ avartans. Building endurance and precision at high speeds." },
  { title: "Improvisation & Jugalbandi", desc: "The art of spontaneous improvisation — answering phrases, rhythmic dialogue with other musicians." },
  { title: "Complex Taals", desc: "Jhoomra (14 beats), Chautaal (12 beats), Ada-Chautaal (14 beats), Dharni (17 beats) and other rare taals." },
  { title: "Solo Recital Design", desc: "Structuring a full-length 45–60 minute concert set — peshkar, kaida, rela, gat sequence." },
  { title: "Certification & Examination", desc: "Preparation for Akhil Bharatiya Gandharva Mahavidyalaya Mandal (ABGMVM) Visharad examination." },
];

const AdvancedCourse = () => {
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-maroon-dark">
      <SEO
        title="Advanced Tabla Course — Concert-Level Tabla Mastery | Taalsetu"
        description="Reach concert-level tabla performance with Taalsetu's advanced course. Master gharana styles, complex taals, solo recital design, and ABGMVM certification preparation with Maestro Atharva Kagalkar."
        keywords="advanced tabla course, tabla mastery, tabla concert performance, tabla gharana, tabla certification India, ABGMVM tabla, tabla solo performance, Atharva Kagalkar advanced tabla, Taalsetu advanced"
        url="https://www.taalsetu.com/courses/advanced/"
        schemaMarkup={[courseSchema, faqSchema]}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.taalsetu.com/' },
          { name: 'Advanced Tabla Course', url: 'https://www.taalsetu.com/courses/advanced/' }
        ]}
      />

      {/* Hero */}
      <motion.section
        className="relative pt-28 pb-20 bg-gradient-to-br from-maroon via-maroon to-maroon-dark text-white overflow-hidden"
        initial="hidden" animate="visible" variants={fadeUp}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center bg-gold/20 border border-gold/30 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FaTrophy className="mr-2" /> Advanced Level
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight mb-6">
            Advanced Tabla —<br />
            <span className="text-gold">Concert-Level Mastery</span>
          </h1>
          <p className="text-lg md:text-xl text-gold-light/80 max-w-2xl mb-10 leading-relaxed">
            For serious students ready for the stage. Deep-dive into gharana styles, complex taals, improvisation, and professional concert performance. The path to mastery starts here.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="btn-gold flex items-center !rounded-xl text-lg px-8 py-4">
              Apply Now <FaArrowRight className="ml-2" />
            </Link>
            <a href="tel:+918275664288" className="flex items-center px-8 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 font-medium text-lg">
              Request Audition
            </a>
          </div>

          <div className="flex flex-wrap gap-8 mt-12">
            {[
              { icon: <FaClock />, label: "1 Year Program" },
              { icon: <FaTrophy />, label: "Certification Prep" },
              { icon: <FaMicrophoneAlt />, label: "Concert Performance" },
              { icon: <FaDrum />, label: "Gharana Style Training" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gold-light/70 text-sm">
                <span className="text-gold">{item.icon}</span> {item.label}
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Who is this for */}
      <motion.section
        className="py-16 bg-gold/5 border-y border-gold/10"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-6 text-center">Who Is the Advanced Course For?</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "You can confidently perform teentaal, keherwa, and at least 3 other taals",
              "You've completed 2+ years of serious tabla study",
              "You want to perform at concerts, sabhas, or cultural festivals",
              "You are preparing for official music examinations (ABGMVM, etc.)",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <FaCheckCircle className="text-gold mt-1 flex-shrink-0" />
                <span className="text-maroon/70 dark:text-gold-light/70 text-sm">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Modules */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">Course Modules</h2>
            <div className="gold-divider mb-4" />
            <p className="text-maroon/60 dark:text-gold-light/60 max-w-2xl mx-auto">
              A year-long intensive programme covering every facet of professional tabla artistry.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((mod, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-maroon/3 dark:bg-white/5 border border-gold/10 rounded-xl p-6 hover:border-gold/30 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-gold/20 rounded-lg flex items-center justify-center text-gold font-bold text-sm">{i + 1}</div>
                  <h3 className="font-serif font-bold text-maroon dark:text-gold-light">{mod.title}</h3>
                </div>
                <p className="text-sm text-maroon/60 dark:text-gold-light/60 leading-relaxed">{mod.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Outcomes */}
      <motion.section
        className="py-20 bg-maroon/5 dark:bg-maroon-dark/50"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">By the End of This Course</h2>
            <div className="gold-divider mb-4" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Perform a complete 45-minute tabla solo concert",
              "Play 8+ classical taals with full command and improvisation",
              "Demonstrate knowledge of multiple gharana styles",
              "Pass ABGMVM Visharad examination (if pursued)",
              "Accompany vocalists, instrumentalists, and classical dancers",
              "Compose and teach your own students at a basic level",
            ].map((point, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-start gap-3">
                <FaStar className="text-gold mt-1 flex-shrink-0 text-lg" />
                <span className="text-maroon/70 dark:text-gold-light/70">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">
              Frequently Asked Questions
            </h2>
            <div className="gold-divider" />
          </motion.div>
          <div className="space-y-5">
            {faqs.map((faq, i) => (
              <motion.div key={i} variants={fadeUp} className="border border-gold/15 rounded-xl p-6 bg-maroon/3 dark:bg-white/5">
                <h3 className="font-serif font-bold text-maroon dark:text-gold-light mb-2">{faq.q}</h3>
                <p className="text-maroon/65 dark:text-gold-light/65 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-20 bg-maroon text-white text-center"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Ready for the Stage?</h2>
          <p className="text-gold-light/70 mb-8 text-lg">Applications are reviewed personally by Maestro Atharva Kagalkar. Limited seats available.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn-gold !rounded-xl px-10 py-4 text-lg flex items-center">
              Apply Now <FaArrowRight className="ml-2" />
            </Link>
            <Link to="/#contact" className="px-10 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 text-lg">
              Contact Us
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default AdvancedCourse;
